import React from 'react';
import { Box, Group, Image, Paper, Stack, Text } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import classes from './ClinicTasksHeader.module.css';

interface IClinicTasksHeaderProps {
  logoUrl?: string | null;
  clinicName?: string;
  count: number;
}

const ClinicTasksHeader: React.FC<IClinicTasksHeaderProps> = ({
  logoUrl,
  clinicName,
  count,
}) => {
  const { t } = useTranslation();
  const isDone = count === 0;

  return (
    <Paper
      w="100%"
      p="md"
      mb="xs"
      radius="lg"
      className={classes.header}
      style={{
        background: 'linear-gradient(180deg, #fbfdff, #f5f8fc)',
        border: '1px solid var(--mantine-color-gray-2)',
      }}
    >
      <Group align="center" gap="md" preventGrowOverflow={false}>
        <Box
          className={classes.logoBox}
          style={{
            flex: '0 0 auto',
            height: 60,
            minWidth: 60,
            '--logo-max-width': logoUrl ? '220px' : '260px',
            display: 'grid',
            placeItems: 'center',
            padding: '8px 14px',
            background: '#fff',
            border: '1px solid var(--mantine-color-gray-2)',
            borderRadius: 12,
            boxShadow: '0 1px 3px rgba(16,24,40,.06)',
          }}
        >
          {logoUrl ? (
            <Image
              src={logoUrl}
              alt={clinicName ?? ''}
              fit="contain"
              h={44}
              w="auto"
              style={{ maxWidth: 192 }}
            />
          ) : (
            <Box
              h={44}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text
                fw={600}
                c="dark.6"
                ta="center"
                lineClamp={2}
                style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: 18,
                  lineHeight: 1.2,
                }}
              >
                {clinicName ?? ''}
              </Text>
            </Box>
          )}
        </Box>

        <Stack
          gap={2}
          className={classes.title}
          style={{ minWidth: 0, alignSelf: 'center' }}
        >
          <Text fw={700} size="md" c="dark.7">
            {t('tasksBoard.tasksToComplete')}
          </Text>
        </Stack>

        <Box
          style={{
            flex: '0 0 auto',
            marginLeft: 'auto',
            textAlign: 'center',
            minWidth: 64,
            padding: '10px 16px',
            borderRadius: 14,
            color: '#fff',
            background: isDone
              ? 'var(--mantine-color-green-7)'
              : 'var(--mantine-color-blue-7)',
            boxShadow: isDone
              ? '0 6px 16px rgba(47,158,68,.3)'
              : '0 6px 16px rgba(28,126,214,.3)',
          }}
        >
          <Text
            fw={700}
            lh={1}
            style={{ fontSize: 26, display: 'flex', justifyContent: 'center' }}
          >
            {isDone ? <IconCheck size={26} stroke={3} /> : count}
          </Text>
          <Text
            fw={600}
            tt="uppercase"
            mt={3}
            style={{ fontSize: 10.5, letterSpacing: '.06em', opacity: 0.92 }}
          >
            {isDone ? t('tasksBoard.allDone') : t('tasksBoard.pending')}
          </Text>
        </Box>
      </Group>
    </Paper>
  );
};

export default ClinicTasksHeader;
